// useWebBlePrint — hook React que ata el transporte Web Bluetooth (BLE), el slice de
// perfiles persistidos y el encoder ESC/POS para imprimir tickets térmicos directo a una
// impresora BLE cuando el panel corre en navegador (NO Electron).
//
// API espejo de `useElectron`: el integrador ramifica los sitios de impresión web a
//   `webBle.isWebBleActive ? webBle.printOrder(...) : pdfMakePrint(...)`.
//
// Reglas críticas:
// - NUNCA se accede a `navigator.bluetooth` en el top-level (rompería jsdom). Todo acceso
//   vive dentro de funciones, detrás de la guarda `isWebBleSupported()`.
// - Antes de CADA impresión se llama `ensureReady` (reconexión silenciosa): las térmicas
//   BLE se duermen entre pedidos. Si no hay conexión viva se intenta `reconnectKnown`
//   (sin diálogo) y, como último recurso, se expone `pairPrinter` para reemparejar dentro
//   de un gesto de usuario.
// - El emparejamiento (`pairPrinter`) abre el chooser del navegador y REQUIERE gesto de
//   usuario; persiste el perfil (deviceId/deviceName) en el slice durable.

import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { OrderDetail } from '@interfaces/OrderTypes';
import { useDetailedOrders } from '@pages/Orders/hooks/orders';
import { appStore } from '@store/index';
import { capturePrintError } from '@utils/print/printSentry';
import { buildWebBleTicket, WebBleTicketOptions } from '@utils/print/webble/buildWebBleTicket';
import { CanvasTicketOptions, renderTicketCanvas } from '@utils/print/webble/canvasTicket';
import { EscPos } from '@utils/print/webble/escpos';
import { useWebBlePrintMode } from '@utils/print/webble/printMode';
import { canvasToEscPosRaster, dotsForWidth, imageToEscPosRaster } from '@utils/print/webble/raster';
import {
  attachDevice,
  ensureReady as ensureReadyTransport,
  getConnection,
  isWebBleSupported as isWebBleSupportedTransport,
  reconnectKnown,
  requestPrinter,
  writeChunked,
} from '@utils/print/webble/bleTransport';
import type { BluetoothDevice } from '../../types/web-bluetooth';
import { useMainWebBlePrinter, useWebBlePrintersActions } from '@store/actions/webBlePrinters';
import type { WebBlePrintSettings } from '@store/slices/webBlePrinters';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

type PrintOrderArgs = {
  detailedOrder?: OrderDetail;
  orderId: string;
  /** Perfil de impresora a usar. Default: la principal (`getMainPrinter`). */
  settings?: WebBlePrintSettings;
  /**
   * Permite abrir un chooser FILTRADO (solo la impresora guardada) como último recurso
   * cuando la reconexión silenciosa falla (p.ej. tras recargar la página y `getDevices`
   * no devuelve el dispositivo). REQUIERE gesto de usuario → solo `true` en impresión
   * manual (botón). En auto-impresión por socket NO hay gesto, así que va en `false`.
   */
  allowPrompt?: boolean;
  /** Clave del perfil, para persistir el nuevo deviceId si se reconecta vía chooser. */
  printerKey?: string;
  /** Interno: dentro de un lote multi-impresora; NO togglear `isPrinting` (lo dueña el lote). */
  keepBatchState?: boolean;
};

type PrintSelfServiceArgs = {
  orderIDs: string[];
  settings?: WebBlePrintSettings;
  allowPrompt?: boolean;
  printerKey?: string;
  keepBatchState?: boolean;
};

// Resultado de intentar resolver (reconectar) un dispositivo:
// - 'connected': hay un device listo para escribir.
// - 'cancelled': el usuario cerró el chooser (NotFoundError/AbortError) → silencio.
// - 'failed': la reconexión falló por otra razón (impresora apagada, chooser sin
//   resultados, sin gesto disponible) → el caller debe avisar al usuario.
type ResolveOutcome = { status: 'connected'; device: BluetoothDevice } | { status: 'cancelled' } | { status: 'failed' };

// Prefijo de nombre estable para filtrar el chooser (las térmicas suelen ser "PT-220_B49C").
const namePrefixOf = (deviceName?: string | null): string | undefined => {
  const name = (deviceName || '').trim();
  if (!name) return undefined;
  return name.split('_')[0] || name;
};

type PairPrinterArgs = {
  /** Perfil al que asignar el dispositivo. Si se omite, se crea uno nuevo. */
  printerKey?: string;
  /** Prefijo de nombre para filtrar el chooser; si se omite, acepta todos. */
  namePrefix?: string;
};

// Construye las opciones de ticket a partir del perfil de impresora + datos del comercio.
const buildTicketOptions = (settings: WebBlePrintSettings): WebBleTicketOptions => {
  const commerce = appStore.getState().commerce;
  return {
    ticketType: settings.ticketType,
    textSize: settings.textSize,
    pageWidth: settings.pageWidth,
    decoration: settings.decoration,
    commerceName: commerce?.nombre,
    commerceAddress: commerce?.address?.fullAddress || commerce?.direccion || '',
    referencesLabel: commerce?.config_form_entrega?.referencias?.label,
    showClientPhone: settings.showClientPhone,
    showCommerceName: settings.showCommerceName,
    showCommerceAddress: settings.showCommerceAddress,
  };
};

// Opciones para el renderer a canvas (imagen): mismas que el ticket de texto +
// `showLogo` y `logoUrl` (el renderer carga y dibuja el logo internamente).
const buildCanvasOptions = (settings: WebBlePrintSettings): CanvasTicketOptions => {
  const commerce = appStore.getState().commerce;
  return {
    ticketType: settings.ticketType,
    textSize: settings.textSize,
    pageWidth: settings.pageWidth,
    decoration: settings.decoration,
    showLogo: settings.showLogo,
    commerceName: commerce?.nombre,
    commerceAddress: commerce?.address?.fullAddress || commerce?.direccion || '',
    logoUrl: commerce?.logotipo ?? null,
    referencesLabel: commerce?.config_form_entrega?.referencias?.label,
    showClientPhone: settings.showClientPhone,
    showCommerceName: settings.showCommerceName,
    showCommerceAddress: settings.showCommerceAddress,
  };
};

// Líneas de avance antes del corte (igual que el feedBeforeCut del encoder de texto).
const FEED_BEFORE_CUT = 4;

// Envuelve el raster (GS v 0 del canvas) en un ticket imprimible completo:
// ESC @ (reset) al inicio, el raster, y feed + corte al final. El corte va SIEMPRE
// DESPUÉS del raster (de lo contrario se cortaría sobre la imagen).
const wrapRasterTicket = (raster: Uint8Array): Uint8Array =>
  new EscPos().raw(raster).feed(FEED_BEFORE_CUT).cut().encode();

// Intenta construir el ticket como IMAGEN (canvas → 1-bit → GS v 0). Devuelve null
// si el entorno no tiene DOM, el logo tainteó por CORS, o algo falló — en cuyo caso
// el caller cae al encoder de texto (buildWebBleTicket).
const tryBuildCanvasBytes = async (order: OrderDetail, settings: WebBlePrintSettings): Promise<Uint8Array | null> => {
  const canvas = await renderTicketCanvas(order, buildCanvasOptions(settings));
  if (!canvas) return null;
  const raster = canvasToEscPosRaster(canvas);
  if (!raster) return null;
  return wrapRasterTicket(raster);
};

// Cache del logo (raster ESC/POS) por URL+ancho: evita re-descargar/convertir en cada
// impresión. Devuelve null si no hay logo, está apagado, o la imagen no es accesible.
const logoRasterCache = new Map<string, Uint8Array | null>();

const resolveLogoRaster = async (printer: WebBlePrintSettings): Promise<Uint8Array | null> => {
  if (!printer.showLogo) return null;
  const commerce = appStore.getState().commerce;
  const url = commerce?.logotipo;
  if (!url) return null;
  const dots = dotsForWidth(printer.pageWidth);
  // Incluir el id del comercio en la llave: la cache vive a nivel de módulo y NO se limpia
  // al cambiar de sesión/comercio, así que sin el id dos comercios con la MISMA URL de logo
  // compartirían el raster (mismo patrón frágil que el bug PR#1020).
  const key = `${commerce?.id ?? ''}|${dots}|${url}`;
  const cached = logoRasterCache.get(key);
  if (cached !== undefined) return cached;
  const raster = await imageToEscPosRaster(url, dots);
  logoRasterCache.set(key, raster);
  return raster;
};

export const useWebBlePrint = () => {
  const [isPrinting, setIsPrinting] = useState(false);
  const { getOrder } = useDetailedOrders();
  const mainPrinter = useMainWebBlePrinter();
  const { updateWebBlePrintSettings, addPrinter } = useWebBlePrintersActions();

  // Telemetría de funnel a GA4 (PostHog off). `trackEvent` de useTracking devuelve nueva
  // referencia por render; lo guardamos en un ref para leer el closure más reciente desde las
  // funciones async sin desestabilizar nada. Patrón de useFeatureVersion (reportado por Seer).
  const { trackEvent } = useTracking();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // Emisión fire-and-forget: el funnel NUNCA debe romper la impresión. Cualquier error de
  // telemetría se traga (Sentry sigue siendo la fuente de verdad para errores de impresión).
  const emitFunnel = (eventName: string, params?: Record<string, unknown>): void => {
    try {
      trackEventRef.current(eventName, params);
    } catch {
      // no-op: la telemetría no debe interferir con la impresión.
    }
  };

  // `isWebBleActive`: el usuario activó el modo Bluetooth (master toggle, opt-in), hay
  // soporte del navegador Y una impresora habilitada y emparejada (deviceId/deviceName
  // para reconectar sin diálogo). Si es false, el sitio de impresión cae al fallback
  // pdfMake (diálogo del navegador) — comportamiento tradicional.
  const { enabled: isBlePrintModeOn } = useWebBlePrintMode();
  const hasPairedPrinter = Boolean(mainPrinter?.enabled && (mainPrinter?.deviceId || mainPrinter?.deviceName));
  const isWebBleActive = isWebBleSupportedTransport() && isBlePrintModeOn && hasPairedPrinter;

  const getMainPrinter = (): WebBlePrintSettings | undefined => mainPrinter;

  // Reconecta (silenciosamente) o reutiliza una conexión viva para un perfil dado y
  // devuelve el device listo. Estrategia (espejo del spike probado contra hardware):
  //   1. Conexión viva en runtime (mismo dispositivo ya abierto).
  //   2. Reconexión SIN diálogo: handle en memoria (misma sesión) o getDevices().
  //   3. Último recurso (solo con gesto de usuario): chooser FILTRADO al nombre guardado
  //      → aparece SOLO su impresora, un toque la reconecta. Cubre el caso "recargué la
  //      página / cerré sesión y getDevices no la devuelve" sin re-emparejar a ciegas.
  // Variante que DISTINGUE el resultado (connected/cancelled/failed) para que el caller
  // decida el feedback (toast). `resolveDevice` (abajo) es el wrapper compatible que solo
  // devuelve el device o null para los flujos de impresión que no necesitan el detalle.
  const resolveDeviceOutcome = async (
    settings: WebBlePrintSettings,
    opts: { allowPrompt?: boolean; printerKey?: string } = {},
  ): Promise<ResolveOutcome> => {
    if (settings.deviceId) {
      const live = getConnection(settings.deviceId);
      if (live && live.device.gatt?.connected) return { status: 'connected', device: live.device };
    }
    const reconnected = await reconnectKnown({
      deviceId: settings.deviceId ?? undefined,
      deviceName: settings.deviceName ?? undefined,
    });
    if (reconnected?.device) {
      // La reconexión silenciosa puede emparejar por NOMBRE un device con id distinto
      // al guardado (el deviceId de Web Bluetooth es volátil entre sesiones). Si difiere,
      // persistir el nuevo id para que la próxima reconexión por getDevices acierte directo.
      if (opts.printerKey && reconnected.device.id && reconnected.device.id !== settings.deviceId) {
        updateWebBlePrintSettings(
          { deviceId: reconnected.device.id, deviceName: reconnected.device.name ?? settings.deviceName },
          opts.printerKey,
        );
      }
      // Reconexión SILENCIOSA exitosa (handle en memoria / getDevices, sin chooser).
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectSuccess, { via: 'silent' });
      return { status: 'connected', device: reconnected.device };
    }

    // Sin gesto de usuario no se puede abrir el chooser: la reconexión silenciosa falló.
    if (!opts.allowPrompt) {
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectFailed, { via: 'silent', reason: 'no_gesture' });
      return { status: 'failed' };
    }
    // Reconexión asistida (requiere gesto): chooser filtrado a la impresora guardada.
    try {
      const prefix = namePrefixOf(settings.deviceName);
      const device = await requestPrinter(prefix ? { namePrefix: prefix } : {});
      await attachDevice(device);
      // El deviceId de Web Bluetooth es volátil entre sesiones: persistir el nuevo para
      // que la PRÓXIMA reconexión silenciosa (getDevices) tenga más chance de acertar.
      if (opts.printerKey) {
        updateWebBlePrintSettings(
          { deviceId: device.id, deviceName: device.name ?? settings.deviceName },
          opts.printerKey,
        );
      }
      // Reconexión ASISTIDA exitosa (chooser filtrado dentro de un gesto de usuario).
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectSuccess, { via: 'prompt' });
      return { status: 'connected', device };
    } catch (error) {
      // Cancelar el chooser (NotFoundError/AbortError) no es una falla real → silencio.
      const name = error instanceof Error ? error.name : '';
      if (name === 'NotFoundError' || name === 'AbortError') {
        return { status: 'cancelled' };
      }
      capturePrintError(error, { tag: 'web_ble_reconnect_prompt_failure', trigger: 'web_ble' });
      emitFunnel(TRACK_EVENT_NAMES.BleReconnectFailed, { via: 'prompt', reason: name || 'unknown' });
      return { status: 'failed' };
    }
  };

  const resolveDevice = async (
    settings: WebBlePrintSettings,
    opts: { allowPrompt?: boolean; printerKey?: string } = {},
  ): Promise<BluetoothDevice | null> => {
    const outcome = await resolveDeviceOutcome(settings, opts);
    return outcome.status === 'connected' ? outcome.device : null;
  };

  // Imprime los bytes de un ticket ya construido, respetando `copies`. Centraliza
  // ensureReady + writeChunked + manejo de errores.
  const printBytes = async (
    settings: WebBlePrintSettings,
    bytes: Uint8Array,
    opts: { allowPrompt?: boolean; printerKey?: string; degradedToText?: boolean } = {},
  ): Promise<boolean> => {
    // `format` define el slice del funnel de impresión (imagen vs texto). El error_rate del
    // rollout = BlePrintFailure / (BlePrintSuccess + BlePrintFailure) por formato.
    const format = settings.ticketFormat;
    // Cuando el formato era imagen y cayó al encoder de texto (CORS/sin DOM), se adjunta a
    // TODOS los eventos del funnel de esta impresión sin contar un fallo extra.
    const degraded = opts.degradedToText ? { degraded_to_text: true } : {};
    if (!isWebBleSupportedTransport()) {
      capturePrintError(new Error('Web Bluetooth no soportado'), {
        tag: 'web_ble_not_supported',
        trigger: 'web_ble',
      });
      emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason: 'not_supported', ...degraded });
      return false;
    }
    const device = await resolveDevice(settings, opts);
    if (!device) {
      capturePrintError(new Error('Impresora BLE no emparejada/reconectable'), {
        tag: 'web_ble_printer_not_paired',
        trigger: 'web_ble',
      });
      // Mensaje según haya o no gesto: en manual sugiere reconectar tocando; en auto avisa
      // que la impresora no respondió (no se puede abrir chooser sin gesto).
      toast.error(
        opts.allowPrompt
          ? 'No se pudo conectar la impresora Bluetooth. Enciéndela y vuelve a intentar.'
          : 'La impresora Bluetooth no respondió. Imprime manualmente o revísala en Ajustes.',
      );
      emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason: 'not_connected', ...degraded });
      return false;
    }
    // Techo de 5 copias: protege contra valores corruptos/excesivos en el perfil.
    const copies = Math.min(5, Math.max(1, settings.copies || 1));
    try {
      const writeChar = await ensureReadyTransport(device);
      for (let i = 0; i < copies; i++) {
        await writeChunked(writeChar, bytes);
      }
    } catch (error) {
      // La escritura BLE falló (impresora apagada a mitad, GATT caído, ensureReady sin char).
      // Emitimos el fallo del funnel y RE-LANZAMOS: el caller (printOrder/printSelfServiceOrder)
      // ya lo captura en Sentry y avisa al usuario. No duplicamos esa lógica aquí.
      const reason = error instanceof Error ? error.name || 'WriteError' : 'WriteError';
      emitFunnel(TRACK_EVENT_NAMES.BlePrintFailure, { format, reason, ...degraded });
      throw error;
    }
    emitFunnel(TRACK_EVENT_NAMES.BlePrintSuccess, { format, copies, ...degraded });
    return true;
  };

  const printOrder = async ({
    detailedOrder,
    orderId,
    settings,
    allowPrompt,
    printerKey,
    keepBatchState,
  }: PrintOrderArgs): Promise<void> => {
    const printer = settings ?? mainPrinter;
    if (!printer) return;
    const opts = { allowPrompt, printerKey };
    try {
      if (!keepBatchState) setIsPrinting(true);
      const order = detailedOrder || (await getOrder(orderId));
      // Camino preferido: ticket renderizado a IMAGEN (canvas → raster GS v 0), para
      // que se vea igual a Electron/pdfMake y soporte los 5 niveles de tamaño por
      // elemento. Si no hay DOM o el logo taintea por CORS, cae al encoder de texto.
      // El formato configurado era IMAGEN pero el canvas no se pudo construir (sin DOM o logo
      // tainteado por CORS) → caemos al encoder de TEXTO. `degradedToText` se adjunta al evento
      // final del funnel (éxito/fallo) en vez de emitir un fallo propio: así NO inflamos el
      // error_rate (attempts == jobs), pero el rollout ve cuántas impresiones perdieron la imagen.
      let degradedToText = false;
      if (printer.ticketFormat === 'image') {
        const canvasBytes = await tryBuildCanvasBytes(order, printer);
        if (canvasBytes) {
          await printBytes(printer, canvasBytes, opts);
          return;
        }
        degradedToText = true;
      }
      const logoRaster = await resolveLogoRaster(printer);
      const bytes = buildWebBleTicket(order, { ...buildTicketOptions(printer), logoRaster });
      await printBytes(printer, bytes, { ...opts, degradedToText });
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble', extra: { orderId } });
      toast.error('Error al imprimir el pedido por Bluetooth');
    } finally {
      if (!keepBatchState) setIsPrinting(false);
    }
  };

  const printSelfServiceOrder = async ({
    orderIDs,
    settings,
    allowPrompt,
    printerKey,
    keepBatchState,
  }: PrintSelfServiceArgs): Promise<void> => {
    const printer = settings ?? mainPrinter;
    if (!printer) return;
    const opts = { allowPrompt, printerKey };
    try {
      if (!keepBatchState) setIsPrinting(true);
      const response = await Promise.all(orderIDs.map((id) => getOrder(id)));
      if (!response || response.length === 0) {
        toast.error('No se encontraron pedidos para imprimir');
        return;
      }
      const sorted = response.sort((a, b) => {
        const aDate = new Date(a?.pedido?.created_at || 0);
        const bDate = new Date(b?.pedido?.created_at || 0);
        return aDate.getTime() - bDate.getTime();
      });
      const orderData: OrderDetail = {
        pedido: {
          ...sorted[0].pedido,
          folio: `mesa: ${sorted[0].pedido?.self_service?.table_number}`,
          numero_mesa: sorted[0].pedido?.self_service?.table_number,
          subtotal: sorted.reduce((acc, order) => acc + Number(order.pedido.subtotal), 0),
          total: sorted.reduce((acc, order) => acc + Number(order.pedido.total), 0),
          coupon_amount: sorted.reduce((acc, order) => acc + Number(order.pedido.coupon_amount), 0),
          comentario: '',
        },
        productos: sorted.map((order) => order.productos).flat(),
      };
      // Misma degradación imagen→texto que printOrder: el flag viaja al evento final del funnel.
      let degradedToText = false;
      if (printer.ticketFormat === 'image') {
        const canvasBytes = await tryBuildCanvasBytes(orderData, printer);
        if (canvasBytes) {
          await printBytes(printer, canvasBytes, opts);
          return;
        }
        degradedToText = true;
      }
      const logoRaster = await resolveLogoRaster(printer);
      const bytes = buildWebBleTicket(orderData, { ...buildTicketOptions(printer), logoRaster });
      await printBytes(printer, bytes, { ...opts, degradedToText });
    } catch (error) {
      capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble', extra: { orderIDs } });
      toast.error('Error al imprimir los pedidos por Bluetooth');
    } finally {
      if (!keepBatchState) setIsPrinting(false);
    }
  };

  // Lista de [printerKey, settings] de TODAS las impresoras habilitadas y emparejadas
  // (con deviceId o deviceName) leídas FRESCAS del store. Es el mismo filtro que usa el
  // auto-print por socket (useIncomingOrder): cada impresora imprime su propio formato.
  const enabledPairedPrinters = (): Array<[string, WebBlePrintSettings]> => {
    const printers = appStore.getState().webBlePrinters ?? {};
    return Object.entries(printers).filter(([, p]) => p.enabled && (p.deviceId || p.deviceName)) as Array<
      [string, WebBlePrintSettings]
    >;
  };

  // Impresión MANUAL multi-impresora: itera TODAS las impresoras habilitadas y emparejadas
  // y manda el pedido a cada una con su propio perfil (ticketType/formato/copias). Replica
  // el modelo del auto-print para que, con Caja+Cocina, ambas reciban en manual (printOrder
  // a secas usa solo la principal). Si no hay ninguna, no-op.
  const printOrderToAll = async ({
    detailedOrder,
    orderId,
    allowPrompt,
  }: {
    detailedOrder?: OrderDetail;
    orderId: string;
    allowPrompt?: boolean;
  }): Promise<void> => {
    const targets = enabledPairedPrinters();
    if (!targets.length) return;
    // `isPrinting` se mantiene encendido durante TODO el lote (no parpadea entre impresoras).
    try {
      setIsPrinting(true);
      for (const [printerKey, settings] of targets) {
        await printOrder({ detailedOrder, orderId, settings, printerKey, allowPrompt, keepBatchState: true });
      }
    } finally {
      setIsPrinting(false);
    }
  };

  // Versión multi-impresora de la impresión de autoservicio (mesa). Mismo modelo que
  // `printOrderToAll`: una copia por cada impresora habilitada y emparejada.
  const printSelfServiceToAll = async ({
    orderIDs,
    allowPrompt,
  }: {
    orderIDs: string[];
    allowPrompt?: boolean;
  }): Promise<void> => {
    const targets = enabledPairedPrinters();
    if (!targets.length) return;
    try {
      setIsPrinting(true);
      for (const [printerKey, settings] of targets) {
        await printSelfServiceOrder({ orderIDs, settings, printerKey, allowPrompt, keepBatchState: true });
      }
    } finally {
      setIsPrinting(false);
    }
  };

  // Abre el chooser del navegador (REQUIERE gesto de usuario), conecta el dispositivo y
  // persiste deviceId/deviceName en el perfil indicado (o crea uno nuevo). Devuelve el
  // perfil resultante o null si el usuario canceló / falló la conexión.
  const pairPrinter = async ({ printerKey, namePrefix }: PairPrinterArgs = {}): Promise<WebBlePrintSettings | null> => {
    if (!isWebBleSupportedTransport()) {
      capturePrintError(new Error('Web Bluetooth no soportado'), {
        tag: 'web_ble_not_supported',
        trigger: 'web_ble',
      });
      toast.error('Este navegador no soporta impresión por Bluetooth.');
      return null;
    }
    // Intento de emparejamiento: el usuario abrió el chooser (dentro de un gesto). Marca el
    // denominador de la tasa de éxito de emparejar (success/cancelled del funnel de pairing).
    emitFunnel(TRACK_EVENT_NAMES.BlePairAttempt);
    try {
      const device = await requestPrinter(namePrefix ? { namePrefix } : {});
      await attachDevice(device);
      const patch: Partial<WebBlePrintSettings> = {
        deviceId: device.id,
        deviceName: device.name ?? null,
        label: device.name ?? 'Impresora Bluetooth',
        enabled: true,
      };
      // Emparejamiento exitoso: dispositivo elegido y GATT conectado (attachDevice OK).
      emitFunnel(TRACK_EVENT_NAMES.BlePairSuccess, { device_name: device.name ?? null });
      toast.success(`Impresora ${device.name ?? 'Bluetooth'} conectada.`);
      // Estado FRESCO del store tras el await del chooser/conexión: el perfil pudo
      // borrarse o cambiar durante la espera (un closure capturado quedaría stale).
      // Leerlo del store evita actualizar/recrear un perfil obsoleto (huérfano).
      const currentPrinters = appStore.getState().webBlePrinters ?? {};
      if (printerKey && currentPrinters[printerKey]) {
        updateWebBlePrintSettings(patch, printerKey);
        return { ...currentPrinters[printerKey], ...patch };
      }
      addPrinter(patch);
      return null; // el nuevo perfil queda en el store; la UI lo re-lee del selector
    } catch (error) {
      // El usuario puede cancelar el chooser → DOMException con name 'NotFoundError'
      // (o 'AbortError' en algunos navegadores). Comparar por `name` es robusto;
      // el string del mensaje varía por navegador/idioma. No es una falla real.
      const name = error instanceof Error ? error.name : '';
      const isUserCancel = name === 'NotFoundError' || name === 'AbortError';
      if (isUserCancel) {
        // El usuario cerró el chooser sin elegir: no es falla, pero cuenta como abandono del
        // emparejamiento (numerador de la tasa de cancelación del funnel de pairing).
        emitFunnel(TRACK_EVENT_NAMES.BlePairCancelled, { reason: name });
      } else {
        capturePrintError(error, { tag: 'web_ble_print_failure', trigger: 'web_ble' });
        toast.error('No se pudo emparejar la impresora Bluetooth.');
      }
      return null;
    }
  };

  // Reconecta una impresora YA emparejada (botón "Reconectar"): primero en silencio
  // (handle en memoria / getDevices) y, si falla, abre un chooser FILTRADO a su impresora
  // dentro del gesto. Devuelve true si quedó conectada. Pensado para el caso del usuario:
  // tras recargar, getDevices puede estar ausente → un toque y listo, sin re-emparejar a ciegas.
  const reconnectPrinter = async ({
    settings,
    printerKey,
  }: {
    settings: WebBlePrintSettings;
    printerKey?: string;
  }): Promise<boolean> => {
    if (!isWebBleSupportedTransport()) {
      toast.error('Este navegador no soporta impresión por Bluetooth.');
      return false;
    }
    const outcome = await resolveDeviceOutcome(settings, { allowPrompt: true, printerKey });
    if (outcome.status === 'connected') {
      toast.success('Impresora confirmada y lista.');
      return true;
    }
    // 'cancelled' (el usuario cerró el chooser) → silencio, no es una falla real.
    if (outcome.status === 'failed') {
      toast.error('No se pudo conectar. Enciende la impresora y vuelve a tocar Confirmar.');
    }
    return false;
  };

  // Reconexión SILENCIOSA (sin gesto): solo intenta el camino sin chooser (handle en memoria /
  // getDevices). NUNCA abre el chooser ni muestra toasts — pensada para el warm-up al abrir
  // Ajustes. Devuelve true si quedó conectada. Si falla, no hace ruido (cae al CTA "Confirmar").
  const silentReconnect = async ({
    settings,
    printerKey,
  }: {
    settings: WebBlePrintSettings;
    printerKey?: string;
  }): Promise<boolean> => {
    if (!isWebBleSupportedTransport()) return false;
    try {
      const outcome = await resolveDeviceOutcome(settings, { allowPrompt: false, printerKey });
      return outcome.status === 'connected';
    } catch {
      return false;
    }
  };

  return {
    isWebBleSupported: isWebBleSupportedTransport(),
    isWebBleActive,
    pairPrinter,
    reconnectPrinter,
    silentReconnect,
    printOrder,
    printSelfServiceOrder,
    printOrderToAll,
    printSelfServiceToAll,
    ensureReady: ensureReadyTransport,
    getMainPrinter,
    status: { isPrinting, hasPairedPrinter },
  };
};
